/**
 * A Markdown field with Write and Preview. Pasting or dropping an image into the text uploads it
 * and inserts ![name](attachment:<id>) where the cursor was; the file is also handed to
 * onUploaded so the caller lists it with the other attachments.
 */
import { useId, useRef, useState } from 'react';
import type { ClipboardEvent, DragEvent, KeyboardEvent, ReactNode } from 'react';
import { attachmentRef } from '@orgus/protocol';
import { cn } from '../lib/cn.ts';
import { toUiError } from '../lib/errors.ts';
import { filesFrom } from './AttachmentInput.tsx';
import type { AttachmentView, UploadFn } from './AttachmentInput.tsx';
import { Markdown } from './Markdown.tsx';
import type { ResolvedAttachment } from './Markdown.tsx';
import { Segmented } from './Segmented.tsx';

export interface MarkdownEditorProps {
  label?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  error?: ReactNode;
  rows?: number;
  maxLength?: number;
  /** Uploads pasted or dropped files; without it, pasting files does nothing special. */
  upload?: UploadFn;
  onUploaded?: (a: AttachmentView) => void;
  resolveAttachment?: (id: string) => ResolvedAttachment | null;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  className?: string;
  textareaRef?: (el: HTMLTextAreaElement | null) => void;
}

const markdownFor = (a: AttachmentView) => (a.isImage ? `![${a.name.replace(/[[\]]/g, '')}](${attachmentRef(a.id)})` : `[${a.name.replace(/[[\]]/g, '')}](${attachmentRef(a.id)})`);

export function MarkdownEditor({
  label,
  value,
  onChange,
  placeholder,
  hint,
  error,
  rows = 6,
  maxLength,
  upload,
  onUploaded,
  resolveAttachment,
  onKeyDown,
  autoFocus,
  className,
  textareaRef,
}: MarkdownEditorProps) {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [busy, setBusy] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const latest = useRef(value);
  latest.current = value;
  const id = useId();

  const insertFiles = (files: File[]) => {
    if (!upload || !files.length) return;
    const el = ref.current;
    const at = el ? el.selectionStart : latest.current.length;
    const end = el ? el.selectionEnd : at;
    const tokens = files.map((f, i) => `![Uploading ${f.name.replace(/[[\]]/g, '')} (${Date.now()}-${i})]()`);
    const before = latest.current.slice(0, at);
    const after = latest.current.slice(end);
    const pad = before && !before.endsWith('\n') ? '\n' : '';
    onChange(`${before}${pad}${tokens.join('\n')}\n${after}`);
    setUploadError(null);
    files.forEach((file, i) => {
      setBusy((n) => n + 1);
      upload(file, { onProgress: () => undefined, signal: new AbortController().signal })
        .then((a) => {
          onChange(latest.current.replace(tokens[i]!, markdownFor(a)));
          onUploaded?.(a);
        })
        .catch((e: unknown) => {
          const u = toUiError(e);
          onChange(latest.current.replace(`${tokens[i]!}\n`, '').replace(tokens[i]!, ''));
          setUploadError(`${file.name} did not upload: ${u.why} ${u.next}`);
        })
        .finally(() => setBusy((n) => n - 1));
    });
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFrom(e.clipboardData?.items);
    if (!files.length || !upload) return;
    e.preventDefault();
    insertFiles(files);
  };

  const onDrop = (e: DragEvent<HTMLTextAreaElement>) => {
    const files = filesFrom(e.dataTransfer?.files);
    if (!files.length || !upload) return;
    e.preventDefault();
    e.stopPropagation();
    insertFiles(files);
  };

  const msg = error ?? (uploadError ? <span className="text-status-danger">{uploadError}</span> : busy ? `Uploading ${busy} ${busy === 1 ? 'file' : 'files'}...` : hint);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-end justify-between gap-2">
        {label != null ? (
          <label htmlFor={id} className="text-ui font-medium text-fg-muted">
            {label}
          </label>
        ) : (
          <span />
        )}
        <Segmented
          label="Editor mode"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'write', label: 'Write' },
            { value: 'preview', label: 'Preview' },
          ]}
        />
      </div>
      {tab === 'write' ? (
        <textarea
          id={id}
          ref={(el) => {
            ref.current = el;
            textareaRef?.(el);
          }}
          rows={rows}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
          onDrop={onDrop}
          onKeyDown={onKeyDown}
          aria-invalid={error != null || undefined}
          aria-describedby={msg != null ? `${id}-msg` : undefined}
          // Prose in the UI font, the same as the text reads at rest: mono is only for code in Preview.
          className="control-field w-full min-w-0 resize-y px-3 py-2 font-sans text-ui"
        />
      ) : (
        <div className="min-h-32 rounded-control border border-border bg-sunken px-3 py-2">
          {value.trim() ? <Markdown source={value} resolveAttachment={resolveAttachment} /> : <p className="text-ui text-fg-subtle">Nothing to preview yet.</p>}
        </div>
      )}
      {msg != null && (
        <p id={`${id}-msg`} className={cn('text-caption', error != null ? 'text-status-danger' : 'text-fg-subtle')}>
          {msg}
        </p>
      )}
    </div>
  );
}
